/**
 * Report Status Vocabulary — presentation only.
 * ------------------------------------------------------------------
 * A ClaimGuard report must never blur three very different reasons a
 * value is absent. Every customer-facing surface resolves its wording
 * through this module so the same reason always reads the same way and
 * a reader can tell, at a glance, who (if anyone) needs to act.
 *
 *   NOT_EVIDENCED       The claim file does not contain the document or
 *                       record that would establish the item. Action sits
 *                       with the submitting party.
 *
 *   COVERAGE_BOUNDARY   An external data source does not cover this
 *                       jurisdiction, record type, or subject. Nothing is
 *                       missing from the file; the data simply does not
 *                       exist within ClaimGuard's connected sources.
 *
 *   VERIFICATION_PENDING  The item is on record, but authoritative
 *                       verification against an official publisher has not
 *                       been completed.
 *
 * None of these states is an adverse finding, and none of them changes a
 * calculation, a canonical value, or a stored record. This module contains
 * wording only.
 */

export type AbsenceState = "NOT_EVIDENCED" | "COVERAGE_BOUNDARY" | "VERIFICATION_PENDING";

interface AbsenceCopy {
  /** Short badge / pill label. Title Case, never more than three words. */
  label: string;
  /** One neutral sentence a reader can act on. */
  sentence: string;
  /** Compact caption for dense tables and meta rows. */
  caption: string;
}

const COPY: Record<AbsenceState, AbsenceCopy> = {
  NOT_EVIDENCED: {
    label: "Not Evidenced in File",
    sentence:
      "The submitted claim file does not contain a document establishing this item. Providing it would allow the item to be evaluated.",
    caption: "No supporting document in the claim file",
  },
  COVERAGE_BOUNDARY: {
    label: "Coverage Boundary",
    sentence:
      "This item falls outside the data sources currently connected to ClaimGuard, so no lookup was performed. This is a limit of source coverage, not an adverse finding.",
    caption: "Outside connected source coverage",
  },
  VERIFICATION_PENDING: {
    label: "Verification Pending",
    sentence:
      "The item is recorded as stated, but independent verification against an official published source has not been completed.",
    caption: "Independent verification not completed",
  },
};

export function absenceLabel(state: AbsenceState): string {
  return COPY[state].label;
}

export function absenceSentence(state: AbsenceState): string {
  return COPY[state].sentence;
}

export function absenceCaption(state: AbsenceState): string {
  return COPY[state].caption;
}

/** Every canonical absence label, for consistency tests and audits. */
export const ABSENCE_LABELS = [
  COPY.NOT_EVIDENCED.label,
  COPY.COVERAGE_BOUNDARY.label,
  COPY.VERIFICATION_PENDING.label,
] as const;

/**
 * Deprecated phrasings that must never reach a customer-facing surface.
 * Kept as data so the presentation audit can assert on it directly.
 */
export const DEPRECATED_ABSENCE_PHRASES = [
  "Insufficient Evidence",
  "Insufficient Documentation",
  "Not Documented in File",
  "Source Unavailable",
  "Not Verified",
  "Verification Not Completed",
  "Not Independently Verified",
  "Awaiting Live Provider",
  "Not Connected",
  "Awaiting Provider Integration",
] as const;

/**
 * Legacy/internal status keys mapped onto the canonical customer-facing
 * absence vocabulary. The keys are internal state names that other modules
 * still compute and store; only the rendered label changes here.
 */
const STATUS_DISPLAY: Record<string, string> = {
  "Not Connected": COPY.COVERAGE_BOUNDARY.label,
  Unsupported: COPY.COVERAGE_BOUNDARY.label,
  "Awaiting Live Provider": COPY.COVERAGE_BOUNDARY.label,
  Unavailable: COPY.VERIFICATION_PENDING.label,
  "Not Verified": COPY.VERIFICATION_PENDING.label,
  "Verification Unavailable": COPY.VERIFICATION_PENDING.label,
  "Source Unavailable": COPY.VERIFICATION_PENDING.label,
  "Not Independently Verified": COPY.VERIFICATION_PENDING.label,
  "Insufficient Evidence": COPY.NOT_EVIDENCED.label,
  "Not Documented in File": COPY.NOT_EVIDENCED.label,
  /* Source-type clarity (presentation only): a reader must be able to tell
     which kind of source produced a value without knowing our internals. */
  "Public Reference": "Evidence Source",
  "Invoice-Derived": "From Submitted Invoice",
  "Internally Classified": "ClaimGuard Database",
  "Historical Data": "ClaimGuard Historical Database",
  "Market Average": "Regional Market Database",
};

/** Renders any internal status key with its approved customer-facing wording. */
export function displayStatusLabel(status: string): string {
  return STATUS_DISPLAY[status] ?? status;
}
